// The full-size image viewer, moved out of App.jsx (2026-10-07) so the reply
// box's attachment preview (SuggestionPanel.jsx) can use it too.
//
// 2026-10-27 (prod contact 8250173; docs/contact-view.md "Photo viewer"): an
// eBay photo is shown at full size (ebayFullSizeImageUrl() in @cxhub/shared;
// the stored URL is a ~400px thumbnail), with Fit / 100% / 200% buttons,
// mouse-wheel and pinch zoom, drag to pan when zoomed, rotate 90 degrees and
// "Open original". Keyboard: + / - zoom, 0 fits, R rotates, Esc closes, the
// arrows move between images. The full-size copy loads right away, never the
// small stored copy first (2026-10-27, Jenn); when it fails to load, the
// stored one is shown with "Showing a smaller copy". The sizing and keyboard
// rules are in lightboxView.js.
import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Dialog, IconButton, Slider, Stack, Typography } from '@mui/material'
import { ebayFullSizeImageUrl } from '@cxhub/shared'
import { fitSize, zoomForPercent, wheelZoom, pinchZoom, clampZoom, nextRotation, isSideways, lightboxKeyAction, canOpenOriginal, initialViewerSrc, fallbackViewerSrc, BUTTON_STEP } from './lightboxView.js'

function LightboxCloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
  )
}
function LightboxLeftIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
  )
}
function LightboxRightIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
  )
}

const OVERLAY_BUTTON_SX = { bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }
const TOOL_BUTTON_SX = { color: '#fff', minWidth: 0, px: 1, py: 0.25, fontSize: 12, textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }

// Full-size image viewer with left/right navigation across every image in the
// conversation (not just the message the clicked thumbnail belonged to) —
// index into `images` ({ url, name }) is the single source of truth for
// what's shown. Arrows disable/hide at the two ends rather than wrapping
// around.
//
// fullResUrl (optional): maps a shown url to its full-size copy. Defaults to
// the eBay picture CDN helper, which leaves any other url (an attachment's
// blob: URL, a data: URL) unchanged; a caller that must show exactly its own
// src (the reply box's attachment preview) passes the identity.
export default function ImageLightbox({ images, index, onClose, onNavigate, fullResUrl = ebayFullSizeImageUrl }) {
  const boxRef = useRef(null)
  // { width, height } of the image shown (the full-size copy, or the stored
  // one after a fallback), measured on its load: the fit (the opening size).
  const [natural, setNatural] = useState(null)
  // { width, height } of the full-size copy once it loads: 100% and 200%.
  const [fullNatural, setFullNatural] = useState(null)
  const [manualZoom, setManualZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [fellBack, setFellBack] = useState(false)
  // What the <img> shows: the full-size URL right away; the stored URL only
  // when the full-size one fails.
  const [displaySrc, setDisplaySrc] = useState(() => { const u = images[index]?.url ?? null; return initialViewerSrc(u, u ? fullResUrl(u) : null) })
  const zoomRef = useRef(1)
  // The latest fullResUrl, so a caller's inline function never resets the view.
  const fullResUrlRef = useRef(fullResUrl)
  fullResUrlRef.current = fullResUrl
  zoomRef.current = manualZoom
  // Pointers down on the viewport, for drag-to-pan and pinch.
  const pointersRef = useRef(new Map())
  const panRef = useRef(null)
  const pinchRef = useRef(null)

  const storedUrl = index != null ? images[index]?.url ?? null : null
  const fullUrl = storedUrl ? fullResUrl(storedUrl) : null

  const navigate = useCallback((to) => onNavigate(to), [onNavigate])

  // Each newly-opened image starts at its own default: fit, upright, no
  // fallback notice.
  useEffect(() => {
    setManualZoom(1)
    setRotation(0)
    setNatural(null)
    setFullNatural(null)
    setFellBack(false)
    pointersRef.current = new Map()
    const u = index != null && images[index] ? images[index].url : null
    setDisplaySrc(initialViewerSrc(u, u ? fullResUrlRef.current(u) : null))
  }, [index, images])

  const box = boxRef.current
  const fit = fitSize(natural, box ? { width: box.clientWidth, height: box.clientHeight } : null, rotation)
  const fullSize = fullNatural ?? natural

  useEffect(() => {
    if (index == null) return undefined
    function onKeyDown(e) {
      const action = lightboxKeyAction(e.key)
      if (!action) return
      if (action === 'close') onClose()
      else if (action === 'prev') { if (index > 0) navigate(index - 1) }
      else if (action === 'next') { if (index < images.length - 1) navigate(index + 1) }
      else if (e.ctrlKey || e.metaKey || e.altKey) return
      else if (action === 'zoom_in') setManualZoom((z) => clampZoom(z * BUTTON_STEP))
      else if (action === 'zoom_out') setManualZoom((z) => clampZoom(z / BUTTON_STEP))
      else if (action === 'fit') setManualZoom(1)
      else if (action === 'rotate') setRotation((r) => nextRotation(r))
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [index, images.length, onClose, navigate])

  // Mouse wheel (and a trackpad pinch, which browsers send as a wheel with
  // ctrlKey) zooms. A native listener: React's onWheel is passive and cannot
  // stop the page from scrolling.
  const [viewport, setViewport] = useState(null)
  const setBoxRef = useCallback((el) => { boxRef.current = el; setViewport(el) }, [])
  useEffect(() => {
    if (!viewport) return undefined
    function onWheel(e) {
      e.preventDefault()
      setManualZoom((z) => wheelZoom(z, e.deltaY))
    }
    viewport.addEventListener('wheel', onWheel, { passive: false })
    return () => viewport.removeEventListener('wheel', onWheel)
  }, [viewport])

  if (index == null || !images[index]) return null

  // The shown image's natural size gives the fit. The full-size copy's own
  // size also gives 100% and 200% (after a fallback, the stored copy's).
  function handleLoad(e) {
    const size = { width: e.target.naturalWidth, height: e.target.naturalHeight }
    setNatural(size)
    if (displaySrc === fullUrl) setFullNatural(size)
  }

  // The full-size copy failed: the stored one, once, with a notice. When the
  // stored copy is the one that failed, nothing more (no retry loop).
  function handleError() {
    const next = fallbackViewerSrc(displaySrc, storedUrl)
    if (next) {
      setFellBack(true)
      setNatural(null)
      setDisplaySrc(next)
    }
  }

  function onPointerDown(e) {
    const el = boxRef.current
    if (!el) return
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()]
      pinchRef.current = { distance: Math.hypot(a.x - b.x, a.y - b.y), zoom: zoomRef.current }
      panRef.current = null
    } else if (pointersRef.current.size === 1 && (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight)) {
      panRef.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop }
      el.setPointerCapture?.(e.pointerId)
    }
  }
  function onPointerMove(e) {
    const el = boxRef.current
    if (!el || !pointersRef.current.has(e.pointerId)) return
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pinchRef.current && pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()]
      setManualZoom(pinchZoom(pinchRef.current.zoom, pinchRef.current.distance, Math.hypot(a.x - b.x, a.y - b.y)))
      return
    }
    if (panRef.current) {
      el.scrollLeft = panRef.current.left - (e.clientX - panRef.current.x)
      el.scrollTop = panRef.current.top - (e.clientY - panRef.current.y)
    }
  }
  function onPointerUp(e) {
    pointersRef.current.delete(e.pointerId)
    if (pointersRef.current.size < 2) pinchRef.current = null
    if (pointersRef.current.size === 0) panRef.current = null
  }

  const hasPrev = index > 0
  const hasNext = index < images.length - 1
  const sideways = isSideways(rotation)
  const renderWidth = fit ? fit.width * manualZoom : null
  const renderHeight = fit ? fit.height * manualZoom : null
  const zoomedPast = Boolean(box && renderWidth && ((sideways ? renderHeight : renderWidth) > box.clientWidth || (sideways ? renderWidth : renderHeight) > box.clientHeight))
  const percent = fit && fullSize?.width ? Math.round((renderWidth / fullSize.width) * 100) : null
  const openable = canOpenOriginal(fullUrl)

  return (
    <Dialog open onClose={onClose} maxWidth={false} fullWidth
      PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible', width: '95vw', maxWidth: '95vw', m: 0 } }}>
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '95vw', height: '92vh' }}>
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, ...OVERLAY_BUTTON_SX }}>
          <LightboxCloseIcon />
        </IconButton>
        {hasPrev && (
          <IconButton aria-label="Previous image" onClick={() => navigate(index - 1)} sx={{ position: 'absolute', left: 8, zIndex: 2, ...OVERLAY_BUTTON_SX }}>
            <LightboxLeftIcon />
          </IconButton>
        )}
        {/* Scrollable viewport: once the zoom pushes the image past the
            visible area it scrolls, and a drag pans it. Centered by margin:
            'auto' on the image, not flex centering: flex centering pushes an
            overflowing image's top and left to negative offsets no scrollbar
            can reach, while auto margins center a small image and collapse
            to 0 once it overflows. */}
        <Box ref={setBoxRef} data-testid="lightbox-viewport"
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
          sx={{ width: '100%', height: '100%', overflow: 'auto', display: 'flex', touchAction: 'none', cursor: zoomedPast ? 'grab' : 'default' }}>
          {/* Before the size is known, the shrink-only maxWidth/maxHeight
              sizing, so there is no flash of an oversized image. Then
              explicit pixels: fit x zoom, never a CSS stretch. Turned
              sideways, a frame takes the turned size and the image turns
              inside it; otherwise the image is the viewport's own child. */}
          {/* key={index}, not the url: a fresh node (and load event) on every
              navigation, even when two images share a src. */}
          {renderWidth && sideways ? (
            <Box sx={{ position: 'relative', flexShrink: 0, margin: 'auto', width: `${renderHeight}px`, height: `${renderWidth}px` }}>
              <Box key={index} component="img" src={displaySrc} alt={images[index].name} draggable={false}
                onLoad={handleLoad} onError={handleError} data-rotation={rotation}
                sx={{ position: 'absolute', left: '50%', top: '50%', width: `${renderWidth}px`, height: `${renderHeight}px`, maxWidth: 'none', transform: `translate(-50%, -50%) rotate(${rotation}deg)`, borderRadius: 1, userSelect: 'none' }} />
            </Box>
          ) : renderWidth ? (
            <Box key={index} component="img" src={displaySrc} alt={images[index].name} draggable={false}
              onLoad={handleLoad} onError={handleError} data-rotation={rotation}
              sx={{ width: `${renderWidth}px`, height: `${renderHeight}px`, flexShrink: 0, margin: 'auto', borderRadius: 1, userSelect: 'none', ...(rotation ? { transform: `rotate(${rotation}deg)` } : {}) }} />
          ) : (
            <Box key={index} component="img" src={displaySrc} alt={images[index].name} draggable={false}
              onLoad={handleLoad} onError={handleError} data-rotation={rotation}
              sx={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', margin: 'auto', borderRadius: 1 }} />
          )}
        </Box>
        {/* The toolbar sits between the arrows in the DOM, so the close
            button stays first and the next arrow last. */}
        <Stack direction="row" spacing={0.5} alignItems="center" data-testid="lightbox-toolbar"
          sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 2, bgcolor: 'rgba(0,0,0,0.6)', borderRadius: 1, px: 1, py: 0.25 }}>
          <Button size="small" aria-label="Fit to screen" onClick={() => setManualZoom(1)} sx={TOOL_BUTTON_SX}>Fit</Button>
          <Button size="small" aria-label="Zoom to 100%" onClick={() => setManualZoom(zoomForPercent(100, fit, fullSize))} sx={TOOL_BUTTON_SX}>100%</Button>
          <Button size="small" aria-label="Zoom to 200%" onClick={() => setManualZoom(zoomForPercent(200, fit, fullSize))} sx={TOOL_BUTTON_SX}>200%</Button>
          <Button size="small" aria-label="Rotate 90 degrees" onClick={() => setRotation((r) => nextRotation(r))} sx={TOOL_BUTTON_SX}>Rotate</Button>
          {openable && (
            <Button size="small" aria-label="Open original in a new tab" onClick={() => window.open(fullUrl, '_blank', 'noopener,noreferrer')} sx={TOOL_BUTTON_SX}>Open original</Button>
          )}
          {percent != null && (
            <Typography variant="caption" data-testid="lightbox-zoom-percent" sx={{ color: '#fff', pl: 0.5 }}>{percent}%</Typography>
          )}
        </Stack>
        {fellBack && (
          <Typography variant="caption" role="status" sx={{ position: 'absolute', top: 52, zIndex: 2, color: '#fff', bgcolor: 'rgba(160,80,0,0.85)', px: 1, py: 0.25, borderRadius: 1 }}>
            Showing a smaller copy
          </Typography>
        )}
        {hasNext && (
          <IconButton aria-label="Next image" onClick={() => navigate(index + 1)} sx={{ position: 'absolute', right: 8, zIndex: 2, ...OVERLAY_BUTTON_SX }}>
            <LightboxRightIcon />
          </IconButton>
        )}
        <Box sx={{ position: 'absolute', bottom: 40, width: 240, bgcolor: 'rgba(0,0,0,0.5)', borderRadius: 1, px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#fff' }}>Zoom</Typography>
          <Slider size="small" min={1} max={5} step={0.1} value={Math.min(Math.max(manualZoom, 1), 5)} aria-label="Zoom"
            onChange={(_e, v) => setManualZoom(v)} sx={{ color: '#fff' }} />
        </Box>
        <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', px: 1, borderRadius: 1 }}>
          {index + 1} / {images.length}
        </Typography>
      </Box>
    </Dialog>
  )
}
